import { useEffect, useState } from "react";
import { Download, Trash2, Upload } from "lucide-react";
import { EmployerLayout } from "@/components/employer/portal/EmployerLayout";
import { apiFetch, EmptyState, formatDate, inputClass, Label, Spinner } from "@/components/employer/portal/shared";

const KINDS = [["business_registration", "Business registration"], ["tax_document", "Tax document"], ["company_profile", "Company profile"], ["authorization", "Authorization letter"], ["other", "Other"]];

export default function EmployerDocuments() {
    const [items, setItems] = useState(null);
    const [kind, setKind] = useState("business_registration");
    const [uploading, setUploading] = useState(false);

    const load = () => apiFetch("/employer/documents").then((r) => (r.ok ? r.json() : [])).then(setItems);
    useEffect(() => { load(); }, []);

    const upload = async (file) => {
        if (!file || uploading) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", file);
        await apiFetch("/employer/documents", { method: "POST", body: fd });
        setUploading(false);
        load();
    };

    const download = async (doc) => {
        const res = await apiFetch(`/employer/documents/${doc.stored}`);
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.original;
        a.click();
        URL.revokeObjectURL(url);
    };

    const remove = async (doc) => {
        await apiFetch(`/employer/documents/${doc.stored}`, { method: "DELETE" });
        load();
    };

    return (
        <EmployerLayout title="Documents" subtitle="Securely store company verification and recruitment documents.">
            <div className="grid gap-8 lg:grid-cols-[0.38fr_0.62fr]" data-testid="employer-documents-page">
                <section className="h-fit border border-ink/10 bg-sand p-6">
                    <Label>Document type</Label>
                    <select data-testid="document-kind" value={kind} onChange={(e) => setKind(e.target.value)} className={`${inputClass} cursor-pointer`}>
                        {KINDS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                    <label className="mt-4 flex cursor-pointer items-center justify-between gap-4 border border-dashed border-ink/25 bg-ivory px-5 py-5 text-sm text-ink/50 transition-colors hover:border-forest">
                        {uploading ? "Uploading…" : "Choose a document to upload"}
                        <Upload size={17} className="shrink-0 text-forest" />
                        <input data-testid="document-upload" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
                    </label>
                    <p className="mt-3 text-xs leading-relaxed text-ink/45">PDF, DOC, DOCX, JPG or PNG — up to 5 MB. Files are private to your employer account and authorized Afrinexa reviewers.</p>
                </section>
                <section>
                    {!items ? <Spinner /> : items.length === 0 ? (
                        <EmptyState title="No documents yet" copy="Upload business registration or supporting documents to help verification move faster." />
                    ) : (
                        <div className="space-y-3" data-testid="documents-list">
                            {items.map((doc) => (
                                <article key={doc.id} data-testid={`document-${doc.id}`} className="flex flex-wrap items-center justify-between gap-4 border border-ink/10 bg-ivory px-5 py-4">
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-medium text-ink">{doc.original}</p>
                                        <p className="mt-1 text-[10px] uppercase tracking-[0.16em] text-ink/40">{doc.kind.replace(/_/g, " ")} · {formatDate(doc.uploaded_at)}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button type="button" data-testid={`document-download-${doc.id}`} onClick={() => download(doc)} className="border border-ink/20 p-2.5 text-ink/55 hover:border-forest hover:text-forest" aria-label="Download document"><Download size={15} /></button>
                                        <button type="button" data-testid={`document-delete-${doc.id}`} onClick={() => remove(doc)} className="border border-ink/15 p-2.5 text-ink/40 hover:border-ink/40 hover:text-ink" aria-label="Delete document"><Trash2 size={15} /></button>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </EmployerLayout>
    );
}
